import * as THREE from 'three';
interface overlayLineProperties {
  containerId?: string;
  color?: string;
  lineWidth?: number;
  opacity?: number;
}

export class OverlayLine {
  private containerId: string;
  private points: THREE.Vector2[];
  private color: string;
  private lineWidth: number;
  private opacity: number;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D | null;
  private container: HTMLElement | null;
  private resizeObserver?: ResizeObserver;

  /**
   * Constructor to initialize the screenLine class
   * @param containerId - The ID of the DOM element to place the canvas in (default: 'potree_render_area')
   * @param color - The initial color of the line (default: '#ff0000')
   * @param lineWidth - The initial width of the line (default: 2)
   * @param opacity - The initial opacity of the line (0.0 to 1.0, default: 1.0)
   */
  constructor(props?: overlayLineProperties) {
    this.points = [];
    this.containerId = props?.containerId || 'potree_render_area';
    this.color = props?.color || '#ff0000';
    this.lineWidth = props?.lineWidth || 2;
    this.opacity = props?.opacity || 1.0;

    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    this.canvas.style.position = 'absolute';
    this.canvas.style.pointerEvents = 'none';

    this.container = document.getElementById(this.containerId);
    if (!this.container) {
      console.warn(`Container with ID "${this.containerId}" not found in the DOM.`);
      return;
    }
    this.container.appendChild(this.canvas);

    // Observe container for resize changes
    this.setupResizeObserver();

    this.resize();
    this.setContextParameters();
  }

  /**
   * Set up a ResizeObserver to listen for container dimension changes
   */
  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this.container) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      this.resizeObserver.observe(this.container);
    }
  }

  private handleResize = () => {
    this.resize();
  };

  private setContextParameters() {
    if (this.context) {
      this.context.strokeStyle = this.color;
      this.context.lineWidth = this.lineWidth;
      this.context.globalAlpha = this.opacity;
    }
  }

  public resize(width: number = this.container?.clientWidth || window.innerWidth, height: number = this.container?.clientHeight || window.innerHeight) {
    this.canvas.width = width;
    this.canvas.height = height;
    this.setContextParameters(); // context params get reset when canvas changes size
  }

  /**
   * Add a point to the list of points to draw lines between
   * @param point - The coordinate (screen space)
   */
  public addPoint(point: THREE.Vector2): void {
    this.points.push(point);
  }

  /**
   * Set the points from an array of Vector2 and redraw the line
   * @param points - An array of THREE.Vector2 to set as the line points
   */
  public setFromPoints(points: THREE.Vector2[]): OverlayLine {
    this.points = points;
    return this;
  }

  /**
   * Remove a point at the specified index
   * @param index - Index of the point to remove
   */
  public removePoint(index: number): void {
    if (index >= 0 && index < this.points.length) {
      this.points.splice(index, 1);
    }
  }

  /**
   * Clear all stored points and clear the canvas
   */
  public clearPoints(): void {
    this.points = [];
    if (this.context) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * Set a new color for the line
   * @param color - A valid CSS color string (e.g. 'ff0000', 'blue', 'rgba(0,0,0,0.5)')
   */
  public setColor(color: string): void {
    this.color = color;
    if (this.context) {
      this.context.strokeStyle = this.color;
    }
  }

  /**
   * Set a new line width
   * @param lineWidth - The width of the line in pixels
   */
  public setLinewidth(lineWidth: number): void {
    this.lineWidth = lineWidth;
    if (this.context) {
      this.context.lineWidth = this.lineWidth;
    }
  }

  /**
   * Set a new opacity for the line
   * @param opacity - A number between 0.0 (fully transparent) and 1.0 (fully opaque)
   */
  public setOpacity(opacity: number): void {
    this.opacity = opacity;
    if (this.context) {
      this.context.globalAlpha = this.opacity;
    }
  }

  /**
   * Redraw the line on the canvas, connecting all stored points
   */
  public draw(): void {
    if (!this.context || this.points.length < 2) return;

    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.context.beginPath();
    this.context.moveTo(this.points[0].x, this.points[0].y);

    for (let i = 1; i < this.points.length; i++) {
      this.context.lineTo(this.points[i].x, this.points[i].y);
    }

    this.context.stroke();
  }

  /**
   * Dispose of the overlay line and remove its canvas from the DOM
   */
  public dispose(): void {
    // Disconnect the resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    // Remove canvas from DOM
    if (this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }

    // Clear references
    this.points = [];
    this.context = null;
    this.container = null;
  }
}
